import { useEffect, useMemo, useRef, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import PdfViewerContainer from "../../../components/PdfViewerContainer";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";

const SELECT = "SELECT";
const DOCUMENT_LIST_ENDPOINT = "/FillMyForm/UID_FORMS/ajax_autofill.php";
const genderOptions = [[SELECT, SELECT], ["MALE", "MALE"], ["FEMALE", "FEMALE"]];
const relationOptions = [[SELECT, SELECT], ["FATHER", "FATHER"], ["MOTHER", "MOTHER"], ["GUARDIAN", "GUARDIAN"], ["HUSBAND", "HUSBAND"], ["WIFE", "WIFE"]];
const yesNoOptions = [["NO", "NO"], ["YES", "YES"]];
const certifierOptions = [[SELECT, SELECT], ["1", "Gazetted Officer - Group A"], ["2", "Village Panchayat Head or Mukhiya"], ["3", "Gazetted Officer - Group B"], ["4", "MP / MLA / MLC / Municipal Councillor"], ["5", "Tehsildar"], ["6", "Head of Recognized Educational Institution"], ["7", "Head of Recognized Shelter Home / Orphanage"], ["8", "EPFO Officer"]];

const personalBase = [
  { name: "name", label: "Applicant Name", maxLength: 50, alphaOnly: true, required: true },
  { name: "gender", label: "Gender", options: genderOptions, required: true, updateFlag: "chkgender" },
  { name: "dob", label: "Date of Birth", type: "date", required: true, updateFlag: "chkdob" },
  { name: "chkdv", label: "Date of Birth Verified", checkbox: true },
  { name: "mobile", label: "Mobile", maxLength: 10, numeric: true, mobile: true, required: true, updateFlag: "chkmobile" },
  { name: "email", label: "Email ID", maxLength: 34, email: true, updateFlag: "chkemail" },
];
const parentFields = [
  { name: "relation", label: "Parent / Guardian Relation", options: relationOptions, required: true, conditional: "under5" },
  { name: "parentname", label: "Parent / Guardian Name", maxLength: 33, alphaOnly: true, required: true, conditional: "under5" },
  { name: "paadhaar", label: "Parent / Guardian Aadhaar", maxLength: 12, numeric: true, aadhaar: true, required: true, conditional: "under5" },
];
const addressFields = [
  { name: "coname", label: "C/o Name", maxLength: 50, alphaOnly: true },
  { name: "add1", label: "Address Line 1", maxLength: 25, required: true },
  { name: "add2", label: "Address Line 2", maxLength: 32, required: true },
  { name: "add3", label: "Address Line 3", maxLength: 33 },
  { name: "add4", label: "Address Line 4", maxLength: 30 },
  { name: "add5", label: "Address Line 5", maxLength: 28 },
  { name: "add6", label: "Address Line 6", maxLength: 36 },
  { name: "city", label: "City", maxLength: 12, required: true },
  { name: "dist", label: "District", maxLength: 21, required: true },
  { name: "state", label: "State", maxLength: 14, required: true },
  { name: "pin", label: "PIN", maxLength: 6, numeric: true, pin: true, required: true },
];
const verificationFields = [
  { name: "vtype", label: "Verification Type", options: [["document", "Document Based"], ["hof", "Head of Family (HoF) Based"]], required: true },
  { name: "poi", label: "Proof of Identity (POI)", required: true, conditional: "document", documentList: "POI" },
  { name: "poa", label: "Proof of Address (POA)", required: true, conditional: "document", documentList: "POA" },
  { name: "pod", label: "Proof of Date of Birth", required: true, conditional: "documentDob", documentList: "DOB" },
  { name: "hofrelation", label: "HOF Relation", options: relationOptions, required: true, conditional: "hof" },
  { name: "hofaadhaarno", label: "HOF Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true, required: true, conditional: "hof" },
  { name: "hofname", label: "HOF Name", maxLength: 33, alphaOnly: true, required: true, conditional: "hof" },
  { name: "por", label: "Proof of Relationship (POR)", required: true, conditional: "hof", documentList: "POR" },
  { name: "pod2", label: "Proof of Date of Birth", required: true, conditional: "hofDob", documentList: "DOB" },
];
const certifierFields = [
  { name: "iscertificate", label: "Use Certifier", options: yesNoOptions, required: true },
  { name: "ctype", label: "Certifier Type", options: certifierOptions, required: true, conditional: "certificate" },
  { name: "cname", label: "Certifier Name", maxLength: 50, alphaOnly: true, required: true, conditional: "certificate" },
  { name: "cdesignation", label: "Designation", maxLength: 50, required: true, conditional: "certificate" },
  { name: "oadd1", label: "Office Address Line 1", maxLength: 50, required: true, conditional: "certificate" },
  { name: "oadd2", label: "Office Address Line 2", maxLength: 50, required: true, conditional: "certificate" },
  { name: "cmobile", label: "Certifier Mobile", maxLength: 10, numeric: true, mobile: true, required: true, conditional: "certificate" },
];
const updateChoices = [
  ["chkbiomatric", "Biometric"], ["chkname", "Name"], ["chkgender", "Gender"], ["chkdob", "Date of Birth"],
  ["chkmobile", "Mobile"], ["chkemail", "Email"], ["chkaddress", "Address"], ["chkdocument", "Document"],
];

function ageFromDob(value) {
  if (!value) return null;
  const dob = new Date(`${value}T00:00:00`); if (Number.isNaN(dob.getTime())) return null;
  const now = new Date(); let age = now.getFullYear() - dob.getFullYear();
  if (now.getMonth() < dob.getMonth() || (now.getMonth() === dob.getMonth() && now.getDate() < dob.getDate())) age -= 1;
  return age;
}
function displayDate(value) { if (!value) return ""; const [y, m, d] = value.split("-"); return y && m && d ? `${d}/${m}/${y}` : value; }

export default function UIDNew() {
  const isUpdate = false;
  const endpoint = isUpdate ? "/FillMyForm/UID_FORMS/Print_Update.php" : "/FillMyForm/UID_FORMS/Print_New.php";
  const formCode = isUpdate ? "UID_UPDATE" : "UID_NEW";
  const personalFields = isUpdate ? [{ name: "aadhaarno", label: "Aadhaar Number", maxLength: 12, numeric: true, aadhaar: true, required: true }, ...personalBase] : personalBase;
  const allFields = useMemo(() => [...personalFields, ...parentFields, ...addressFields, ...verificationFields, ...certifierFields], [isUpdate]);
  const initialData = useMemo(() => Object.fromEntries([...allFields.map((f) => [f.name, f.checkbox ? false : (f.options || f.documentList) ? SELECT : ""]), ...updateChoices.map(([name]) => [name, false])]), [allFields]);
  const [formData, setFormData] = useState(() => { try { return { ...initialData, ...JSON.parse(sessionStorage.getItem(`smart-solutions-${formCode.toLowerCase()}`) || "{}") }; } catch { return initialData; } });
  const [currentTab, setCurrentTab] = useState(0), [maxAllowedTab, setMaxAllowedTab] = useState(0);
  const [errors, setErrors] = useState({}), [pdfUrl, setPdfUrl] = useState(""), [showPdf, setShowPdf] = useState(false), [pdfLoading, setPdfLoading] = useState(false), [pdfError, setPdfError] = useState("");
  const [documentLists, setDocumentLists] = useState({ POI: [], POA: [], DOB: [], POR: [] });
  const [documentsLoading, setDocumentsLoading] = useState(true), [documentsError, setDocumentsError] = useState("");
  const nextButtonRef = useRef(null);
  const underFive = ageFromDob(formData.dob) !== null && ageFromDob(formData.dob) < 5;
  const addressEnabled = !isUpdate || formData.chkaddress;

  const active = (field) => {
    if (isUpdate && addressFields.some(({ name }) => name === field.name) && !formData.chkaddress) return false;
    if (isUpdate && field.updateFlag && !formData[field.updateFlag]) return false;
    if (field.conditional === "under5") return underFive;
    if (field.conditional === "document") return formData.vtype === "document";
    if (field.conditional === "documentDob") return formData.vtype === "document" && formData.chkdv;
    if (field.conditional === "hof") return formData.vtype === "hof";
    if (field.conditional === "hofDob") return formData.vtype === "hof" && formData.chkdv;
    if (field.conditional === "certificate") return formData.iscertificate === "YES";
    return true;
  };
  const tabs = useMemo(() => [
    { title: "Applicant", heading: "👤 Applicant Personal Details", fields: [...personalFields, ...parentFields] },
    ...(addressEnabled ? [{ title: "Address", heading: "🏠 Applicant Address Details", fields: addressFields }] : []),
    { title: "Verification", heading: "📄 Verification Type", fields: verificationFields },
    { title: "Certifier", heading: "🖊️ Certifier Details", fields: certifierFields },
    { title: "Preview", heading: "✅ Preview", fields: [] },
  ], [addressEnabled, personalFields]);
  const visibleFields = tabs[currentTab]?.fields || [], enabledFields = visibleFields.filter(active);
  const firstName = enabledFields[0]?.name, lastName = enabledFields.at(-1)?.name;

  useEffect(() => { sessionStorage.setItem(`smart-solutions-${formCode.toLowerCase()}`, JSON.stringify(formData)); }, [formData, formCode]);
  useEffect(() => { if (currentTab >= tabs.length) setCurrentTab(tabs.length - 1); }, [tabs.length, currentTab]);
  useEffect(() => { if (!firstName) return; const id = requestAnimationFrame(() => document.getElementById(firstName)?.focus()); return () => cancelAnimationFrame(id); }, [currentTab, firstName]);
  useEffect(() => () => { if (pdfUrl) URL.revokeObjectURL(pdfUrl); }, [pdfUrl]);
  useEffect(() => {
    const controller = new AbortController();
    (async () => { try {
      setDocumentsLoading(true); setDocumentsError("");
      const response = await fetch(DOCUMENT_LIST_ENDPOINT, { method: "GET", credentials: "include", cache: "no-store", headers: { Accept: "application/json" }, signal: controller.signal });
      const result = await response.json();
      if (!response.ok || !result.success) throw new Error(result.message || "Unable to load the Aadhaar document list.");
      setDocumentLists({ POI: result.documents?.POI || [], POA: result.documents?.POA || [], DOB: result.documents?.DOB || [], POR: result.documents?.POR || [] });
    } catch (e) { if (e.name !== "AbortError") setDocumentsError(e.message || "Unable to load the Aadhaar document list."); } finally { if (!controller.signal.aborted) setDocumentsLoading(false); } })();
    return () => controller.abort();
  }, []);


  useEffect(() => {
    const controller = new AbortController();

    const loadAutofill = async () => {
      try {
        const response = await fetch(
          `/backend/api/autofill.php?form=${encodeURIComponent(formCode)}`,
          {
            method: "GET",
            credentials: "include",
            cache: "no-store",
            headers: {
              Accept: "application/json",
            },
            signal: controller.signal,
          }
        );

        const result = await response.json();

        if (!response.ok || !result.success) {
          throw new Error(result.message || "Unable to load autofill details.");
        }

        if (!result.found || !result.data) {
          return;
        }

        setFormData((previous) => ({
          ...previous,

          city: previous.city || result.data.city || "",
          dist: previous.dist || result.data.dist || "",
          state: previous.state || result.data.state || "",
          pin: previous.pin || result.data.pin || "",
        }));
      } catch (error) {
        if (error.name !== "AbortError") {
          console.error("Autofill error:", error);
        }
      }
    };

    loadAutofill();

    return () => {
      controller.abort();
    };
  }, [formCode]);

  const updateValue = (field, raw) => {
    let value = field.numeric ? String(raw).replace(/\D/g, "") : raw;

    if (field.alphaOnly && typeof value === "string") {
      value = value.replace(/[^A-Za-z ]/g, "");
    }

    setFormData((p) => ({ ...p, [field.name]: value }));
    setErrors((p) => ({ ...p, [field.name]: "" }));
    setPdfError("");
  };
  const errorFor = (field) => {
    if (!active(field)) return "";

    const value = String(formData[field.name] ?? "").trim();

    if (field.required && (!value || value === SELECT)) {
      return `${field.label} is required.`;
    }

    if (!value || value === SELECT) return "";

    if (field.alphaOnly && !/^[A-Za-z ]+$/.test(value)) {
      return `${field.label} must contain alphabet letters only.`;
    }

    if (field.mobile && !/^\d{10}$/.test(value)) {
      return "Enter a valid 10-digit mobile number.";
    }

    if (field.aadhaar && !/^\d{12}$/.test(value)) {
      return "Aadhaar number must contain exactly 12 digits.";
    }

    if (field.pin && !/^\d{6}$/.test(value)) {
      return "PIN must contain exactly 6 digits.";
    }

    if (field.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      return "Enter a valid email address.";
    }

    return "";
  };
  const validate = (fields, validateUpdateChoices = false) => { const next = {}; fields.forEach((f) => { const e = errorFor(f); if (e) next[f.name] = e; }); if (isUpdate && validateUpdateChoices && !updateChoices.some(([n]) => formData[n])) next.updateChoices = "Select at least one detail to update."; setErrors(next); return next; };
  const goNext = () => { if (Object.keys(validate(visibleFields, currentTab === 0)).length) return; const next = Math.min(currentTab + 1, tabs.length - 1); setCurrentTab(next); setMaxAllowedTab((v) => Math.max(v, next)); scrollTo({ top: 0, behavior: "smooth" }); };
  const submit = async () => { const validationErrors = validate(allFields, true); if (Object.keys(validationErrors).length) { const firstErrorName = Object.keys(validationErrors)[0]; const errorTab = firstErrorName === "updateChoices" ? 0 : tabs.findIndex((tab) => tab.fields.some((field) => field.name === firstErrorName)); const targetTab = errorTab >= 0 ? errorTab : 0; setCurrentTab(targetTab); setPdfError(Object.values(validationErrors)[0]); requestAnimationFrame(() => { document.getElementById(firstErrorName)?.focus(); scrollTo({ top: 0, behavior: "smooth" }); }); return; } setPdfLoading(true); setPdfError(""); try { const body = new FormData(); Object.entries(formData).forEach(([k, v]) => { if (typeof v === "boolean") { if (v) body.append(k, "on"); } else body.append(k, v === SELECT ? "" : String(v ?? "")); }); const response = await fetch(endpoint, { method: "POST", body, credentials: "include", cache: "no-store", headers: { Accept: "application/pdf" } }); const type = response.headers.get("content-type") || ""; if (!response.ok || !type.toLowerCase().includes("application/pdf")) { const t = await response.text(); throw new Error(t.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim() || `PDF generation failed. HTTP status: ${response.status}`); } const url = URL.createObjectURL(await response.blob()); setPdfUrl((old) => { if (old) URL.revokeObjectURL(old); return url; }); setShowPdf(true); } catch (e) { setPdfError(e.message || "Unable to generate PDF preview."); } finally { setPdfLoading(false); } };

  const optionsFor = (field) => field.documentList ? [[SELECT, SELECT], ...(documentLists[field.documentList] || []).map((value) => [value, value])] : field.options;
  const renderField = (field) => {
    if (!active(field)) return null;
    const error = errors[field.name];
    const options = optionsFor(field);

    if (field.checkbox) {
      return <label key={field.name} className="flex items-center gap-3 rounded-lg border p-3"><input id={field.name} name={field.name} type="checkbox" checked={!!formData[field.name]} onChange={(e) => updateValue(field, e.target.checked)} className="h-5 w-5" /><span>{field.label}</span></label>;
    }

    return <div key={field.name} className="space-y-2">
      <Label htmlFor={field.name}>{field.label}{field.required && <span className="text-red-500"> *</span>}</Label>
      {field.documentList ? (
        <select
          id={field.name}
          name={field.name}
          value={formData[field.name] || SELECT}
          disabled={documentsLoading}
          onChange={(e) => updateValue(field, e.target.value)}
          className={`flex h-10 w-full rounded-md border bg-background px-3 py-2 text-sm ${error ? "border-red-500" : "border-input"}`}
        >
          {options.map(([value, label]) => <option key={`${field.name}-${value}`} value={value}>{label}</option>)}
        </select>
      ) : options ? (
        <Select value={formData[field.name]} onValueChange={(value) => updateValue(field, value)}>
          <SelectTrigger id={field.name} className={error ? "border-red-500" : ""} onKeyDown={(e) => { if (field.name === lastName && e.key === "Tab" && !e.shiftKey) { e.preventDefault(); nextButtonRef.current?.focus(); } }}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>{options.map(([value, label]) => <SelectItem key={`${field.name}-${value}`} value={value}>{label}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Input id={field.name} name={field.name} type={field.type || "text"} value={formData[field.name]} maxLength={field.maxLength} inputMode={field.numeric ? "numeric" : undefined} placeholder={field.label} onChange={(e) => updateValue(field, e.target.value)} onKeyDown={(e) => { if (field.name === lastName && e.key === "Tab" && !e.shiftKey) { e.preventDefault(); nextButtonRef.current?.focus(); } }} className={error ? "border-red-500" : ""} autoComplete="on" />
      )}
      {error && <p className="text-sm text-red-500">{error}</p>}
    </div>;
  };

  if (showPdf && pdfUrl) return <PdfViewerContainer pdfUrl={pdfUrl} title={`Aadhaar ${isUpdate ? "Update" : "New"} Form`} backRoute="/Fill_My_Form" onBack={() => setShowPdf(false)} />;
  const groups = tabs.filter((t) => t.fields.length).map((tab) => ({ title: tab.title, rows: tab.fields.filter(active).map((f) => [f.label, f.name === "dob" ? displayDate(formData[f.name]) : optionsFor(f)?.find(([v]) => v === formData[f.name])?.[1] || formData[f.name]]) }));
  return <div className="min-h-screen bg-background p-6"><div className="mx-auto max-w-7xl"><Card className="mb-6"><CardHeader><CardTitle className="text-3xl">🪪 Aadhaar {isUpdate ? "Update" : "New Enrolment"} Form</CardTitle><p className="text-muted-foreground">Fill all applicable details carefully.</p></CardHeader></Card>
    <div className="mb-8 grid grid-cols-2 gap-3 md:grid-cols-5">{tabs.map((t, i) => <Button key={t.title} type="button" variant={currentTab === i ? "default" : "outline"} disabled={i > maxAllowedTab || pdfLoading} onClick={() => setCurrentTab(i)}>{i + 1}. {t.title}</Button>)}</div>
    {currentTab === 0 && isUpdate && <Card className="mb-6"><CardHeader><CardTitle>Details to Update</CardTitle></CardHeader><CardContent><div className="grid grid-cols-2 gap-3 md:grid-cols-4">{updateChoices.map(([name, label]) => <label key={name} className="flex items-center gap-2 rounded-lg border p-3"><input type="checkbox" checked={!!formData[name]} onChange={(e) => setFormData((p) => ({ ...p, [name]: e.target.checked }))} className="h-5 w-5" />{label}</label>)}</div>{errors.updateChoices && <p className="mt-3 text-sm text-red-500">{errors.updateChoices}</p>}</CardContent></Card>}
    {currentTab < tabs.length - 1 ? <Card><CardHeader><CardTitle>{tabs[currentTab].heading}</CardTitle></CardHeader><CardContent><div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">{visibleFields.map(renderField)}</div></CardContent></Card> : <Card><CardHeader><CardTitle>✅ Preview</CardTitle></CardHeader><CardContent className="space-y-8">{groups.map((g) => <Preview key={g.title} {...g} />)}</CardContent></Card>}
    {documentsError && <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">{documentsError}</div>}{pdfLoading && <div className="mt-6 text-center">Generating PDF, please wait...</div>}{pdfError && <div className="mt-6 rounded-lg border border-red-500 p-4 text-red-500">{pdfError}</div>}
    <div className="mt-8 flex justify-between"><Button type="button" variant="outline" disabled={!currentTab || pdfLoading} onClick={() => setCurrentTab((v) => Math.max(0, v - 1))}>← Previous</Button>{currentTab === tabs.length - 1 ? <Button type="button" disabled={pdfLoading} onClick={submit}>{pdfLoading ? "Generating PDF..." : "Submit"}</Button> : <Button ref={nextButtonRef} type="button" disabled={pdfLoading} onClick={goNext}>{currentTab === tabs.length - 2 ? "Preview" : "Next →"}</Button>}</div>
  </div></div>;
}
function Preview({ title, rows }) { return <div className="rounded-2xl border p-6"><h3 className="mb-6 text-center text-xl font-semibold">{title}</h3><div className="grid grid-cols-1 gap-4 md:grid-cols-2">{rows.map(([label, value]) => <div key={label} className="grid grid-cols-2 gap-3 border-b pb-2"><div className="font-medium text-muted-foreground">{label}</div><div>{typeof value === "boolean" ? (value ? "Yes" : "No") : value && value !== SELECT ? value : "-"}</div></div>)}</div></div>; }
